import { Audio, AudioListener, AudioLoader, PositionalAudio } from 'three';
import { clamp } from '../utils/math.js';
import { resolveAsset } from '../utils/url.js';

// Soft saturation adds the brassy edge a filtered sawtooth pair lacks.
const HORN_CURVE = new Float32Array(1024);
for (let i = 0; i < HORN_CURVE.length; i += 1) {
  const x = (i / (HORN_CURVE.length - 1)) * 2 - 1;
  HORN_CURVE[i] = Math.tanh(x * 1.9) / Math.tanh(1.9);
}

// Engine saturation is gentler: enough to make the layered oscillators bite
// like a small four-cylinder, without turning the exhaust into a fuzz pedal.
const ENGINE_CURVE = new Float32Array(2048);
for (let i = 0; i < ENGINE_CURVE.length; i += 1) {
  const x = (i / (ENGINE_CURVE.length - 1)) * 2 - 1;
  ENGINE_CURVE[i] = Math.tanh(x * 1.7);
}

/**
 * Minimal game audio manager built on Three.js' WebAudio wrappers.
 *
 * Load buffers once, then fire non-positional sounds (UI, player) or attach
 * positional sounds to scene objects.
 */
export class AudioManager {
  constructor({ masterVolume = 0.8, engine = {} } = {}) {
    this.listener = new AudioListener();
    this.loader = new AudioLoader();
    this.buffers = new Map();
    this.volume = masterVolume;
    this.muted = false;
    this.listener.setMasterVolume(masterVolume);

    this.engineConfig = {
      idleRpm: 850,
      maxRpm: 5200,
      idleVolume: 0.035,
      speedVolume: 0.04,
      loadVolume: 0.045,
      ...engine,
    };
    this.engine = null;
    this._engineClock = 0;
  }

  /** Add the listener to the camera so positional audio has a reference. */
  attachTo(object) {
    object.add(this.listener);
  }

  /** Call from a user gesture (e.g. pressing Start) to unlock WebAudio. */
  unlock() {
    const context = this.listener.context;
    if (context?.state === 'suspended') context.resume();
  }

  async load(name, path) {
    const buffer = await this.loader.loadAsync(resolveAsset(path));
    this.buffers.set(name, buffer);
    return buffer;
  }

  play(name, { volume = 1, loop = false, rate = 1 } = {}) {
    const buffer = this.buffers.get(name);
    if (!buffer) {
      console.warn(`[Audio] No buffer loaded under "${name}".`);
      return null;
    }

    const sound = new Audio(this.listener);
    sound.setBuffer(buffer);
    sound.setVolume(volume);
    sound.setLoop(loop);
    sound.setPlaybackRate(rate);
    sound.play();
    return sound;
  }

  /**
   * Create a positional emitter attached to an Object3D. The caller owns the
   * returned sound and its parent object.
   */
  playAt(name, parent, { volume = 1, loop = false, rate = 1, refDistance = 4 } = {}) {
    const buffer = this.buffers.get(name);
    if (!buffer) {
      console.warn(`[Audio] No buffer loaded under "${name}".`);
      return null;
    }

    const sound = new PositionalAudio(this.listener);
    sound.setBuffer(buffer);
    sound.setVolume(volume);
    sound.setLoop(loop);
    sound.setPlaybackRate(rate);
    sound.setRefDistance(refDistance);
    parent.add(sound);
    sound.play();
    return sound;
  }

  /**
   * Ambient crowd at the fuel queue, played from a recording (crowd.mp3)
   * rather than the old synthesized caws. The game keeps a single looped
   * instance alive and drives its volume with proximity, stopping it when out
   * of range.
   *
   * @param {object} [options]
   * @param {number} [options.volume] starting level, 0..1
   * @param {boolean} [options.loop]  loop the recording (default true)
   * @returns {import('three').Audio|null} the playing emitter, or null if the
   *   buffer failed to load.
   */
  playCrowd({ volume = 0, loop = true } = {}) {
    const sound = this.play('crowd', { volume, loop });
    // The game drives the level every frame via a direct gain assignment, so
    // seed the gain here to match (setVolume would schedule a frozen-clock
    // ramp in headless environments).
    if (sound) sound.gain.gain.value = volume;
    return sound;
  }

  setVolume(volume) {
    this.volume = volume;
    this.listener.setMasterVolume(this.muted ? 0 : volume);
  }

  /**
   * Keep the player engine note in step with the truck.
   *
   * This is synthesized rather than loaded from a recording: a four-cylinder
   * firing frequency drives four detuned voices through saturation and a
   * throttle-controlled low-pass, with a quiet band-passed noise layer for
   * intake/exhaust texture. Road speed sets the base RPM and throttle adds a
   * rev, so idling, pulling away and a full-throttle run all sound different.
   *
   * `active` is false outside the playing state; the note fades out rather
   * than cutting off when the game pauses.
   */
  updateEngine(
    { active = false, throttle = 0, reversing = false, speed = 0, maxSpeed = 26 } = {},
    dt = 1 / 60,
  ) {
    const context = this.listener.context;
    if (!context || context.state === 'closed') return;

    if (!active) {
      this.stopEngine();
      return;
    }

    if (!this.engine) this.#createEngine(context);
    const engine = this.engine;

    if (!engine.active) {
      // A fresh start (or a resume) needs an immediate update so the gain
      // ramps up instead of waiting for the rate-limited path below.
      engine.active = true;
      engine.hasUpdate = false;
    }

    const step = Number.isFinite(dt) ? Math.max(0, dt) : 0;
    this._engineClock += step;
    if (engine.hasUpdate && this._engineClock < 1 / 30) return;
    this._engineClock = 0;

    const {
      idleRpm,
      maxRpm,
      idleVolume,
      speedVolume,
      loadVolume,
    } = this.engineConfig;
    const throttleValue = Number.isFinite(throttle) ? throttle : 0;
    const speedValue = Number.isFinite(speed) ? Math.abs(speed) : 0;
    const topSpeed = Number.isFinite(maxSpeed) && maxSpeed > 0 ? maxSpeed : 26;
    const load = clamp(
      Math.max(0, throttleValue) +
        (reversing ? Math.max(0, -throttleValue) : 0),
      0,
      1,
    );
    const speedRatio = clamp(speedValue / topSpeed, 0, 1);
    const rpm = clamp(
      idleRpm + speedRatio * (maxRpm - idleRpm) * 0.72 + load * 1300,
      idleRpm,
      maxRpm,
    );
    // A four-stroke four-cylinder fires twice per crank revolution.
    const firing = (rpm / 60) * 2;
    const now = context.currentTime;
    const glide = 0.09;

    for (const voice of engine.voices) {
      voice.oscillator.frequency.setTargetAtTime(
        firing * voice.ratio,
        now,
        glide,
      );
    }

    engine.lowpass.frequency.setTargetAtTime(
      420 + speedRatio * 950 + load * 850,
      now,
      0.12,
    );
    engine.noiseFilter.frequency.setTargetAtTime(
      610 + speedRatio * 700 + load * 700,
      now,
      0.12,
    );

    this.#setEngineVolume(
      idleVolume + speedRatio * speedVolume + load * loadVolume,
      0.1,
    );
    engine.noiseGain.gain.setTargetAtTime(
      0.007 + speedRatio * 0.009 + load * 0.022,
      now,
      0.1,
    );
    engine.hasUpdate = true;
  }

  /** Fade the engine note out when leaving the playing state. */
  stopEngine() {
    const engine = this.engine;
    if (!engine || !engine.active) return;
    engine.active = false;
    this.#setEngineVolume(0, 0.07);
  }

  #setEngineVolume(value, timeConstant) {
    const engine = this.engine;
    const context = this.listener.context;
    if (!engine || !context) return;

    engine.output.gain.setTargetAtTime(
      Math.max(0, value),
      context.currentTime,
      Math.max(0.01, timeConstant),
    );
  }

  #createEngine(context) {
    const now = context.currentTime;

    const output = context.createGain();
    output.gain.setValueAtTime(0, now);
    output.connect(this.listener.getInput());

    const saturation = context.createWaveShaper();
    saturation.curve = ENGINE_CURVE;
    saturation.oversample = '2x';

    const lowpass = context.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(420, now);
    lowpass.Q.setValueAtTime(0.8, now);

    const mix = context.createGain();
    mix.gain.setValueAtTime(0.55, now);
    mix.connect(saturation);
    saturation.connect(lowpass);
    lowpass.connect(output);

    const voices = [];
    const addVoice = (waveform, ratio, level, detune = 0) => {
      const oscillator = context.createOscillator();
      oscillator.type = waveform;
      oscillator.frequency.setValueAtTime(28.3 * ratio, now);
      oscillator.detune.setValueAtTime(detune, now);

      const voiceGain = context.createGain();
      voiceGain.gain.setValueAtTime(level, now);
      oscillator.connect(voiceGain);
      voiceGain.connect(mix);
      oscillator.start(now);
      voices.push({ oscillator, ratio });
    };

    // Half-order sub, firing fundamental, its octave and a quiet edge
    // harmonic. The small detunes keep the note from sounding like a synth
    // drone instead of a mechanically driven engine.
    addVoice('triangle', 0.5, 0.18);
    addVoice('sawtooth', 1, 0.3, 3);
    addVoice('sawtooth', 2, 0.38, -4);
    addVoice('square', 3, 0.12, 6);

    // Looping noise gives the intake/exhaust a breathy texture. It is
    // band-passed and mixed under the tones, so it never becomes a hiss.
    const noiseBuffer = context.createBuffer(
      1,
      Math.max(1, Math.floor(context.sampleRate * 2)),
      context.sampleRate,
    );
    const samples = noiseBuffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) {
      samples[i] = Math.random() * 2 - 1;
    }

    const noise = context.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;

    const noiseFilter = context.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.setValueAtTime(610, now);
    noiseFilter.Q.setValueAtTime(0.7, now);

    const noiseGain = context.createGain();
    noiseGain.gain.setValueAtTime(0, now);
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(output);
    noise.start(now);

    this.engine = {
      output,
      lowpass,
      noiseFilter,
      noiseGain,
      noise,
      voices,
      active: true,
      hasUpdate: false,
    };
    this._engineClock = 0;
  }

  /**
   * One electric car horn. Two slightly detuned, filter-shaped oscillators
   * give the dual-tone blat of a real horn without sequencing or layering
   * multiple vehicles.
   */
  horn({ volume = 0.12, duration = 0.38 } = {}) {
    const context = this.listener.context;
    if (!context || context.state !== 'running') return;

    const start = context.currentTime + 0.004;
    const end = start + Math.max(0.12, duration);
    const output = this.listener.getInput();

    const envelope = context.createGain();
    envelope.gain.setValueAtTime(0, start);
    envelope.gain.linearRampToValueAtTime(volume, start + 0.012);
    envelope.gain.setValueAtTime(volume, end - 0.05);
    envelope.gain.linearRampToValueAtTime(0, end);

    const highpass = context.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.setValueAtTime(170, start);
    highpass.Q.setValueAtTime(0.7, start);

    const lowpass = context.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(1750, start);
    lowpass.Q.setValueAtTime(0.9, start);

    const shaper = context.createWaveShaper();
    shaper.curve = HORN_CURVE;
    shaper.oversample = '2x';

    const mix = context.createGain();
    mix.gain.setValueAtTime(0.7, start);

    mix.connect(shaper);
    shaper.connect(highpass);
    highpass.connect(lowpass);
    lowpass.connect(envelope);
    envelope.connect(output);

    // A car horn is one instrument even when it has two tuned trumpets.
    for (const [frequency, level, detune] of [
      [415, 0.5, -4],
      [523.25, 0.42, 3],
    ]) {
      const oscillator = context.createOscillator();
      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(frequency * 0.992, start);
      oscillator.frequency.exponentialRampToValueAtTime(frequency, start + 0.03);
      oscillator.detune.setValueAtTime(detune, start);

      const voice = context.createGain();
      voice.gain.setValueAtTime(level, start);
      oscillator.connect(voice);
      voice.connect(mix);
      oscillator.start(start);
      oscillator.stop(end);
    }
  }

  /**
   * One heavy 12.7 mm machine-gun report: a short noise crack with a low
   * pressure thump underneath. Used by the checkpoint DShK gunners.
   */
  gunshot({ volume = 0.3 } = {}) {
    const context = this.listener.context;
    if (!context || context.state !== 'running') return;

    const start = context.currentTime + 0.003;
    const output = this.listener.getInput();
    const duration = 0.32;
    const noiseBuffer = context.createBuffer(
      1,
      Math.floor(context.sampleRate * duration),
      context.sampleRate,
    );
    const samples = noiseBuffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) {
      const t = i / samples.length;
      const crack = Math.exp(-t * 30);
      const tail = Math.exp(-t * 5.5) * 0.35;
      samples[i] = (Math.random() * 2 - 1) * (crack + tail);
    }

    const noise = context.createBufferSource();
    noise.buffer = noiseBuffer;
    const noiseFilter = context.createBiquadFilter();
    noiseFilter.type = 'lowpass';
    noiseFilter.frequency.setValueAtTime(2200, start);
    noiseFilter.frequency.exponentialRampToValueAtTime(340, start + 0.26);
    noiseFilter.Q.setValueAtTime(0.8, start);
    const noiseGain = context.createGain();
    noiseGain.gain.setValueAtTime(volume, start);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(output);
    noise.start(start);
    noise.stop(start + duration);

    const thump = context.createOscillator();
    thump.type = 'triangle';
    thump.frequency.setValueAtTime(120, start);
    thump.frequency.exponentialRampToValueAtTime(42, start + 0.2);
    const thumpGain = context.createGain();
    thumpGain.gain.setValueAtTime(volume * 0.95, start);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, start + 0.24);
    thump.connect(thumpGain);
    thumpGain.connect(output);
    thump.start(start);
    thump.stop(start + 0.25);
  }

  /**
   * Short synthetic rotor thrum when an RSF drone arrives. Two low oscillators
   * through a tremolo approximate the blade slap, with a filtered noise wash
   * for the air being chopped.
   */
  droneBuzz({ volume = 0.14, duration = 1.6 } = {}) {
    const context = this.listener.context;
    if (!context || context.state !== 'running') return;

    const start = context.currentTime + 0.01;
    const length = Math.max(0.6, duration);
    const end = start + length;
    const output = this.listener.getInput();

    const envelope = context.createGain();
    envelope.gain.setValueAtTime(0, start);
    envelope.gain.linearRampToValueAtTime(volume, start + 0.14);
    envelope.gain.setValueAtTime(volume, end - 0.32);
    envelope.gain.exponentialRampToValueAtTime(0.0001, end);
    envelope.connect(output);

    const tremolo = context.createGain();
    tremolo.gain.setValueAtTime(0.6, start);
    tremolo.connect(envelope);
    const lfo = context.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(13.5, start);
    const lfoGain = context.createGain();
    lfoGain.gain.setValueAtTime(0.34, start);
    lfo.connect(lfoGain);
    lfoGain.connect(tremolo.gain);
    lfo.start(start);
    lfo.stop(end);

    const lowpass = context.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(680, start);
    lowpass.Q.setValueAtTime(0.9, start);
    lowpass.connect(tremolo);

    for (const [frequency, level, type] of [
      [54, 0.22, 'triangle'],
      [81, 0.14, 'triangle'],
      [162, 0.045, 'sawtooth'],
    ]) {
      const oscillator = context.createOscillator();
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, start);
      const voice = context.createGain();
      voice.gain.setValueAtTime(level, start);
      oscillator.connect(voice);
      voice.connect(lowpass);
      oscillator.start(start);
      oscillator.stop(end);
    }

    const noiseBuffer = context.createBuffer(
      1,
      Math.floor(context.sampleRate * length),
      context.sampleRate,
    );
    const samples = noiseBuffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) {
      const fade = Math.sin((i / samples.length) * Math.PI);
      samples[i] = (Math.random() * 2 - 1) * fade;
    }
    const noise = context.createBufferSource();
    noise.buffer = noiseBuffer;
    const noiseFilter = context.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.setValueAtTime(820, start);
    noiseFilter.Q.setValueAtTime(0.6, start);
    const noiseGain = context.createGain();
    noiseGain.gain.setValueAtTime(0.22, start);
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(envelope);
    noise.start(start);
    noise.stop(end);
  }

  /** A rocket leaving the drone's pod: a short, falling jet whoosh. */
  rocketLaunch({ volume = 0.24 } = {}) {
    const context = this.listener.context;
    if (!context || context.state !== 'running') return;

    const start = context.currentTime + 0.004;
    const end = start + 0.68;
    const output = this.listener.getInput();
    const safeVolume = Math.max(0.001, volume);

    const noiseBuffer = context.createBuffer(
      1,
      Math.floor(context.sampleRate * 0.68),
      context.sampleRate,
    );
    const samples = noiseBuffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) {
      const t = i / samples.length;
      samples[i] = (Math.random() * 2 - 1) * Math.exp(-t * 3.1);
    }

    const noise = context.createBufferSource();
    noise.buffer = noiseBuffer;
    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(1500, start);
    filter.frequency.exponentialRampToValueAtTime(360, end);
    filter.Q.setValueAtTime(0.8, start);
    const noiseGain = context.createGain();
    noiseGain.gain.setValueAtTime(0.0001, start);
    noiseGain.gain.linearRampToValueAtTime(safeVolume, start + 0.04);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, end);
    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(output);
    noise.start(start);
    noise.stop(end);

    const motor = context.createOscillator();
    motor.type = 'sawtooth';
    motor.frequency.setValueAtTime(185, start);
    motor.frequency.exponentialRampToValueAtTime(52, end);
    const motorGain = context.createGain();
    motorGain.gain.setValueAtTime(safeVolume * 0.55, start);
    motorGain.gain.exponentialRampToValueAtTime(0.0001, end);
    motor.connect(motorGain);
    motorGain.connect(output);
    motor.start(start);
    motor.stop(end);
  }

  /** A deeper, longer blast than the DShK report, for rocket impacts. */
  explosion({ volume = 0.5 } = {}) {
    const context = this.listener.context;
    if (!context || context.state !== 'running') return;

    const start = context.currentTime + 0.003;
    const duration = 1.15;
    const end = start + duration;
    const output = this.listener.getInput();
    const safeVolume = Math.max(0.001, volume);

    const noiseBuffer = context.createBuffer(
      1,
      Math.floor(context.sampleRate * duration),
      context.sampleRate,
    );
    const samples = noiseBuffer.getChannelData(0);
    for (let i = 0; i < samples.length; i += 1) {
      const t = i / samples.length;
      const crack = Math.exp(-t * 30) * 0.9;
      const rumble = Math.exp(-t * 3.2);
      samples[i] = (Math.random() * 2 - 1) * (crack + rumble);
    }

    const noise = context.createBufferSource();
    noise.buffer = noiseBuffer;
    const lowpass = context.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(1800, start);
    lowpass.frequency.exponentialRampToValueAtTime(90, end);
    lowpass.Q.setValueAtTime(0.7, start);
    const noiseGain = context.createGain();
    noiseGain.gain.setValueAtTime(safeVolume, start);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, end);
    noise.connect(lowpass);
    lowpass.connect(noiseGain);
    noiseGain.connect(output);
    noise.start(start);
    noise.stop(end);

    const sub = context.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(92, start);
    sub.frequency.exponentialRampToValueAtTime(27, start + 0.75);
    const subGain = context.createGain();
    subGain.gain.setValueAtTime(safeVolume * 0.95, start);
    subGain.gain.exponentialRampToValueAtTime(0.0001, start + 0.9);
    sub.connect(subGain);
    subGain.connect(output);
    sub.start(start);
    sub.stop(start + 0.92);
  }

  setMuted(muted) {
    this.muted = muted;
    this.listener.setMasterVolume(muted ? 0 : this.volume);
  }

  dispose() {
    const engine = this.engine;
    if (engine) {
      const now = this.listener.context?.currentTime ?? 0;
      engine.output.gain.cancelScheduledValues(now);
      engine.output.gain.setValueAtTime(0, now);

      for (const { oscillator } of engine.voices) {
        try {
          oscillator.stop(now);
        } catch {
          /* the context may already be closed */
        }
        oscillator.disconnect();
      }
      try {
        engine.noise.stop(now);
      } catch {
        /* the context may already be closed */
      }
      engine.noise.disconnect();
      engine.noiseFilter.disconnect();
      engine.noiseGain.disconnect();
      engine.lowpass.disconnect();
      engine.output.disconnect();
      this.engine = null;
    }

    this.listener.removeFromParent();
    this.buffers.clear();
  }
}
